/**
 * React hooks for accessing feature flags
 * @module lib/feature-flags/useFeatureFlags
 */

import React, { useContext } from "react";
import FeatureFlagContext from "./FeatureFlagContext";

/**
 * Hook to get specific feature flag values
 * @param {string[]} flagKeys - Array of flag keys to retrieve
 * @returns {Array} Array of flag values in the same order as flagKeys
 *
 * @example
 * const [isFeatureEnabled, experimentVariant] = useFeatureFlags([
 *   flags.FLAG_FEAT_MY_FEATURE,
 *   flags.FLAG_EXP_BUTTON_COLOR
 * ]);
 *
 * if (isFeatureEnabled) {
 *   return <NewFeature variant={experimentVariant} />;
 * }
 */
const useFeatureFlags = (flagKeys = []) => {
  const featureFlags = useAllFeatureFlags();

  return flagKeys.reduce((agg, key) => {
    return [...agg, featureFlags[key]];
  }, []);
};

/**
 * Hook to get all feature flags as an object
 * @returns {Object} Object mapping all flag keys to their values
 *
 * @example
 * const allFlags = useAllFeatureFlags();
 * console.log('Current flags:', allFlags);
 */
const useAllFeatureFlags = () => {
  return useContext(FeatureFlagContext);
};

/**
 * Component that conditionally renders children based on a feature flag
 * @param {Object} props - Component props
 * @param {string} props.flag - Flag key to check
 * @param {React.ReactNode} props.children - Children to render if flag is truthy
 * @returns {React.ReactElement|null} Children if flag is truthy, null otherwise
 *
 * @example
 * <ShowFeatureWithFlags flag={flags.FLAG_FEAT_NEW_DASHBOARD}>
 *   <NewDashboard />
 * </ShowFeatureWithFlags>
 */
const ShowFeatureWithFlags = (props) => {
  const [canUse] = useFeatureFlags([props.flag]);
  const children = canUse ? props.children : null;

  return <>{children}</>;
};

export { useFeatureFlags, useAllFeatureFlags, ShowFeatureWithFlags };
